import React, { useEffect, useMemo, useState } from 'react';
import { Inputs, DEFAULT_INPUTS, calculateModel } from '../utils/roiCalculations';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsPageProps {
  isPreview?: boolean;
}
export function ResultsPage({
  isPreview = false
}: ResultsPageProps) {
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  useEffect(() => {
    if (isPreview) {
      const loadInputs = () => {
        const stored = localStorage.getItem('previewInputs');
        if (stored) {
          try {
            setInputs(JSON.parse(stored));
          } catch (e) {
            console.error('Failed to parse preview inputs', e);
          }
        }
      };
      loadInputs();
      const interval = setInterval(loadInputs, 500);
      return () => clearInterval(interval);
    } else {
      const stored = localStorage.getItem('calculatorInputs');
      if (stored) {
        try {
          setInputs(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse calculator inputs', e);
        }
      }
    }
  }, [isPreview]);
  const results = useMemo(() => {
    return calculateModel(inputs);
  }, [inputs]);
  const testimonials = [{
    name: 'Sarah Johnson',
    company: 'Apex Solar',
    role: 'VP of Sales',
    quote: 'HIRE transformed our recruiting process. We went from struggling to find quality reps to building a team of 50+ high performers in just 6 months.',
    image: 'https://i.pravatar.cc/150?img=1'
  }, {
    name: 'Michael Chen',
    company: 'Premier Windows',
    role: 'CEO',
    quote: 'The ROI was undeniable. Within 90 days, our new hires were generating revenue that more than covered our investment.',
    image: 'https://i.pravatar.cc/150?img=2'
  }, {
    name: 'Jessica Martinez',
    company: 'GreenScape Landscaping',
    role: 'Director of Operations',
    quote: "We tried Indeed on our own for years with mediocre results. HIRE's system helped us finally crack the code on consistent, quality hiring.",
    image: 'https://i.pravatar.cc/150?img=3'
  }, {
    name: 'David Thompson',
    company: 'Elite Pest Control',
    role: 'Founder',
    quote: 'The retention rates speak for themselves. Our reps stay longer and perform better than ever before.',
    image: 'https://i.pravatar.cc/150?img=4'
  }, {
    name: 'Amanda Lee',
    company: 'Skyline Roofing',
    role: 'Head of Recruiting',
    quote: 'HIRE took the guesswork out of recruiting. Their system is proven, repeatable, and delivers results every single month.',
    image: 'https://i.pravatar.cc/150?img=5'
  }, {
    name: 'Robert Garcia',
    company: 'Bright Energy Solutions',
    role: 'VP of Growth',
    quote: 'We scaled from 10 to 75 sales reps in under a year. HIRE made it possible without sacrificing quality.',
    image: 'https://i.pravatar.cc/150?img=6'
  }];
  const BookCallButton = () => <a href="https://project-hiredeck.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-base md:text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-640">
      Book My Free Strategy Call
    </a>;
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-641">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-642">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-center" data-id="element-643">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12" data-id="element-644" />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6 md:py-12" data-id="element-645">
        {/* Hero Section */}
        <div id="hero" className="text-center mb-8 md:mb-12" data-id="element-646">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-647">
            Your Personalized ROI Projection
          </h1>
          <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto" data-id="element-648">
            {inputs.companyName}, here's what your recruiting investment could
            generate over the next 12 months
          </p>
        </div>

        {/* Video Section */}
        <div id="video" className="mb-8 md:mb-12" data-id="element-649">
          <div className="max-w-4xl mx-auto" data-id="element-650">
            <div className="aspect-video bg-gradient-to-br from-navy to-navy-light rounded-lg flex items-center justify-center border-2 border-gray-200 shadow-xl" data-id="element-651">
              <div className="text-center text-white p-6 md:p-8" data-id="element-652">
                <svg className="w-12 h-12 md:w-20 md:h-20 mx-auto mb-3 md:mb-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-653">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" data-id="element-654" />
                </svg>
                <p className="text-lg md:text-xl font-semibold" data-id="element-655">
                  Explainer Video
                </p>
                <p className="text-blue-200 mt-2 text-sm md:text-base" data-id="element-656">
                  *will be replaced with a new VSL*
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-657">
            <BookCallButton data-id="element-658" />
          </div>
        </div>

        {/* Results Summary */}
        <div id="summary" className="mb-8 md:mb-12" data-id="element-659">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-660">
            Your Numbers at a Glance
          </h2>
          <ResultsSummary results={results} data-id="element-661" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-662">
            <BookCallButton data-id="element-663" />
          </div>
        </div>

        {/* ROI Chart */}
        <div id="roi-chart" className="mb-8 md:mb-12" data-id="element-664">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-665">
            Your 12-Month ROI Trajectory
          </h2>
          <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-666" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-667">
            <BookCallButton data-id="element-668" />
          </div>
        </div>

        {/* Detailed Projections */}
        <div id="projections" className="mb-8 md:mb-12" data-id="element-669">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-670">
            Month-by-Month Breakdown
          </h2>
          <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-671" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-672">
            <BookCallButton data-id="element-673" />
          </div>
        </div>

        {/* Key Insights */}
        <div id="insights" className="mb-8 md:mb-12" data-id="element-674">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-675">
            Key Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-676">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-677">
              <div className="text-3xl md:text-4xl font-bold text-amber mb-2" data-id="element-678">
                {results.breakeven}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-679">
                Break-Even Point
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-680">
                When your revenue exceeds your total investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-681">
              <div className="text-3xl md:text-4xl font-bold text-green-600" data-id="element-682">
                {fmtPct(results.roi)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-683">
                12-Month ROI
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-684">
                Return on your recruiting investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-685">
              <div className="text-3xl md:text-4xl font-bold text-navy" data-id="element-686">
                {results.hiresPerMonth.toFixed(0)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-687">
                Hires Per Month
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-688">
                Consistent, predictable hiring volume
              </p>
            </div>
          </div>
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-689">
            <BookCallButton data-id="element-690" />
          </div>
        </div>

        {/* Testimonials */}
        <div id="testimonials" className="mb-8 md:mb-12" data-id="element-691">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-692">
            What Our Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-693">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-694">
                <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4" data-id="element-695">
                  <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 md:w-12 md:h-12 rounded-full" data-id="element-696" />
                  <div data-id="element-697">
                    <div className="font-bold text-navy text-sm md:text-base" data-id="element-698">
                      {testimonial.name}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600" data-id="element-699">
                      {testimonial.role}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-700">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-700 italic" data-id="element-701">
                  "{testimonial.quote}"
                </p>
              </div>)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-br from-navy to-navy-light rounded-lg p-6 md:p-12 text-center text-white shadow-xl mb-8 md:mb-12" data-id="element-702">
          <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4" data-id="element-703">
            Ready to Achieve These Results?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 max-w-2xl mx-auto" data-id="element-704">
            Book a free strategy call to discuss how HIRE can help you build a
            high-performing sales team with predictable ROI.
          </p>
          <a href="https://project-hiredeck.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-705">
            Book My Free Strategy Call
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-navy border-t-2 border-white/10" data-id="element-706">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-id="element-707">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6" data-id="element-708">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8" data-id="element-709" />
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-blue-200" data-id="element-710">
              <button onClick={() => scrollToSection('hero')} className="hover:text-white transition-colors" data-id="element-711">
                Overview
              </button>
              <button onClick={() => scrollToSection('video')} className="hover:text-white transition-colors" data-id="element-712">
                Video
              </button>
              <button onClick={() => scrollToSection('summary')} className="hover:text-white transition-colors" data-id="element-713">
                Summary
              </button>
              <button onClick={() => scrollToSection('roi-chart')} className="hover:text-white transition-colors" data-id="element-714">
                ROI Chart
              </button>
              <button onClick={() => scrollToSection('projections')} className="hover:text-white transition-colors" data-id="element-715">
                Projections
              </button>
              <button onClick={() => scrollToSection('insights')} className="hover:text-white transition-colors" data-id="element-716">
                Insights
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors" data-id="element-717">
                Testimonials
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}